// ============================================================
// WEAPON SPECIALS - Pure functions, zero React dependency
// ============================================================
import { rollDie } from './dice';
import type { DiceResult, DiceGroup } from '../types';
import deepClone from '../utils/deepClone';

/** Category: 'R' = ranged-only, 'M' = melee-only, 'C' = common (both) */
export type WSCategory = 'R' | 'M' | 'C';

/**
 * Family: the picker's grouping axis (v8.277.0). Orthogonal to category -
 * category says WHO may take a rule, family says WHAT it touches. Every
 * family must own at least one WS_LIST row (tested), so families for rule
 * sets that land later in the wave arrive together with their rows.
 */
export type WSFamily =
  | 'accuracy'
  | 'firepower'
  | 'damage'
  | 'psy'
  | 'defence'
  | 'stance'
  | 'ammo'
  | 'fields'
  | 'bane'
  | 'predicates'
  | 'conditions'
  | 'crits'
  | 'hordes'
  | 'drains'
  | 'geometry';

/** Display order for the picker's group headers. */
export const WS_FAMILIES: { id: WSFamily; label: string }[] = [
  { id: 'accuracy', label: 'To-hit & range' },
  { id: 'firepower', label: 'Fire modes & hits' },
  { id: 'damage', label: 'Damage & penetration' },
  { id: 'psy', label: 'Psychic' },
  { id: 'defence', label: 'Parry & dodge' },
  { id: 'stance', label: 'Stances' },
  { id: 'ammo', label: 'Ammo & reliability' },
  { id: 'fields', label: 'Fields & wards' },
  { id: 'bane', label: 'Armour & banes' },
  { id: 'predicates', label: 'Against the foe' },
  { id: 'conditions', label: 'Conditions' },
  { id: 'crits', label: 'Criticals & fury' },
  { id: 'hordes', label: 'Hordes' },
  { id: 'drains', label: 'Stat drains' },
  { id: 'geometry', label: 'Templates & geometry' },
];

export const WS_LIST: [string, number, string, WSCategory, WSFamily][] = [
  ['Accurate', 0, 'Aim: +10 hit; aimed single +1d10/2 DoS', 'R', 'accuracy'],
  ['Armourbane', 0, 'AP counts as 0 (mundane)', 'C', 'bane'],
  ['Balanced', 0, '+10 Parry', 'M', 'defence'],
  ['Baleful', 0, 'Bypasses daemonic protection', 'C', 'bane'],
  ['Brawling', 1, '+X hit at PB/Short range', 'R', 'accuracy'],
  // Blurb caught up with area saturation (2026-08-31): the burst's full
  // hit count strikes every victim - "no multi-hit" was the old ruling.
  ['Blessed', 0, 'Never jams', 'R', 'ammo'],
  ['Breaching', 0, 'Ignores machine plating', 'C', 'bane'],
  ['Butchering', 1, '+X dmg vs the already-critted', 'C', 'predicates'],
  ['Channeling', 1, 'Psy rating +X for Force math', 'C', 'psy'],
  ['Cleaving', 0, 'Parried: half damage still lands', 'M', 'defence'],
  ['Concussive', 1, 'Tough -10/X or Stun; dmg>SB Prone', 'C', 'conditions'],
  ['Corrosive', 0, '1d10 AP loss; excess wounds', 'C', 'bane'],
  ['Cowing', 1, 'Willpower bonus -X, 3 rds (tests -10 each)', 'C', 'drains'],
  ['Coverbane', 0, 'Ignores cover AP', 'C', 'bane'],
  // No parameter: the X was declared for years but never read by any
  // combat code - the UI showed a number box that did nothing (v8.196.5).
  ['Crippling', 0, 'Crits also Cripple', 'C', 'crits'],
  ['Cruel', 1, '+X dmg vs prone/stunned/snared/pinned/blinded', 'C', 'predicates'],
  ['Cursed', 0, 'Righteous Fury confirms itself', 'C', 'crits'],
  ['Cycling', 1, 'Recharges X rounds between shots', 'R', 'ammo'],
  ['Deadly', 1, 'Critical severity +X', 'C', 'crits'],
  ['Defensive', 0, '+15 Parry, -10 attack', 'M', 'defence'],
  ['Devastating', 1, '+X Horde Magnitude dmg', 'C', 'hordes'],
  ['Disarming', 1, 'X+ DoS: Str test or no Parry 1 rd', 'M', 'defence'],
  ['Ethereal', 0, 'Parried only by field/force weapons', 'M', 'defence'],
  ['Erratic', 1, 'Blast scatter distance +Xm', 'R', 'geometry'],
  ['Exorcist', 0, 'Can touch the Incorporeal', 'C', 'bane'],
  ['Felling', 1, 'Reduce Unnat. Toughness by X', 'C', 'damage'],
  ['Feinting', 1, 'Defender reactions at -X', 'C', 'defence'],
  ['Fieldbreaker', 0, 'Ignores force fields', 'C', 'fields'],
  ['Flame', 0, 'Agility test or catch Fire', 'R', 'conditions'],
  ['Fleshbane', 0, 'Ignores natural armour', 'C', 'bane'],
  ['Flexible', 0, 'Cannot be parried', 'M', 'defence'],
  ['Force', 0, 'Psyker +psy dmg AND Pen', 'C', 'psy'],
  ['Frugal', 1, 'Ammo cost ÷X (min 1 round)', 'R', 'ammo'],
  ['Graviton', 1, 'Dmg = target AP at loc × X', 'R', 'damage'],
  ['Headsman', 1, '+X dmg below half wounds', 'C', 'predicates'],
  ['Guided', 1, 'Blast scatter distance -Xm', 'R', 'geometry'],
  ['Guzzling', 1, 'Ammo cost ×X', 'R', 'ammo'],
  ['Harrying', 1, 'Dodger takes -X to attacks 1 rd', 'C', 'defence'],
  ['Haywire', 1, '1d10+X on the field table', 'R', 'bane'],
  ['Hexed', 0, 'Its afflictions cannot be resisted', 'C', 'conditions'],
  ['Impetus', 1, 'Charge grants +X more', 'M', 'stance'],
  ['Inaccurate', 0, 'No bonus from Aim', 'R', 'accuracy'],
  ['Indirect', 1, 'No-LoS fire at -10, min range X', 'R', 'geometry'],
  ['Lance', 0, 'Pen × DoS', 'R', 'damage'],
  ['Maximal', 0, '+1d10 dmg, +2 Pen, Blast+2, 3x ammo', 'R', 'firepower'],
  ['Melta', 0, 'Double Pen at Short Range', 'R', 'damage'],
  ['Merciful', 0, 'Never crits; target stabilises at 0', 'C', 'crits'],
  ['Momentum', 1, '+X dmg per hit, growing: 2nd +X, 3rd +2X', 'C', 'predicates'],
  ['Mono', 0, '+2 Penetration', 'M', 'damage'],
  ['NaturalWeapon', 0, 'Immune to Power Field', 'C', 'fields'],
  // Optional param since v8.282.0: Overheats(X) cooks off on X+ instead
  // of 91+ - bare stored strings keep the classic threshold.
  ['Opportunist', 1, '+X dmg vs the unwounded', 'C', 'predicates'],
  ['Numbing', 1, 'WS & BS -X points for 3 rds', 'C', 'drains'],
  ['Nullifying', 1, 'Psy Rating -X for the scene', 'C', 'drains'],
  ['Overheats', 1, 'X+ (default 91): drop, or arm takes wpn dmg', 'R', 'ammo'],
  ['Overloader', 1, 'Blocked: field overload window +X', 'C', 'fields'],
  ['Panicking', 1, 'Damaged horde: WP or X more rout', 'C', 'hordes'],
  ['Pestbane', 0, 'No to-hit penalty vs small prey', 'C', 'accuracy'],
  // Common since v8.277.0: the handler never checked weapon type, so a
  // chainaxe's scream can Pin exactly as suppressive fire does (ADR).
  ['Pinning', 0, 'Hit: WP test or Pinned', 'C', 'conditions'],
  ['Poised', 1, 'Guarded Attack penalty -X', 'M', 'stance'],
  ['PowerField', 0, 'Parry 26+: destroy weapon', 'M', 'fields'],
  ['Primitive', 1, 'Dice above X count as X', 'C', 'damage'],
  ['Purgatus', 0, 'Ignores condition immunities', 'C', 'conditions'],
  ['Purifier', 1, '+X dmg & Pen vs daemons/warp-born', 'C', 'predicates'],
  ['Proven', 1, 'Dice below X count as X', 'C', 'damage'],
  ['Quickening', 1, '+X Initiative while carried', 'C', 'accuracy'],
  ['RazorSharp', 0, '2+ DoS: double Pen', 'C', 'damage'],
  ['Reaping', 1, 'Melee vs hordes: +X hits', 'M', 'hordes'],
  ['Reckless', 1, 'All-Out Attack grants +X more', 'M', 'stance'],
  // Enforced since v8.289.0: reads as Cycling(1). Kept for stored
  // weapons; prefer Cycling(X) for new ones.
  ['Recharge', 0, "Can't fire next turn (= Cycling 1)", 'R', 'ammo'],
  ['Reliable', 0, 'Jam on 00 only', 'R', 'ammo'],
  ['Requiem', 1, '+X dmg vs the Undying', 'C', 'predicates'],
  ['Sanctified', 0, 'Full dmg to Daemons', 'C', 'bane'],
  ['Scrapper', 1, '+X dmg vs the Machine', 'C', 'predicates'],
  ['Sapping', 1, 'Strength bonus -X, 3 rds (tests -10 each)', 'C', 'drains'],
  ['Scatter', 0, 'PB: +10, +1 hit/2 DoS; Long+: AP x2', 'R', 'firepower'],
  ['Scrambler', 1, 'Field protection rating -X', 'C', 'fields'],
  ['Seeking', 0, 'Ignores cover and line of sight', 'R', 'geometry'],
  ['Shock', 0, 'Tough test or Stunned DoF rds', 'C', 'conditions'],
  ['Sighted', 1, 'Aim grants +X more', 'R', 'stance'],
  ['Slowing', 1, 'Agility -X points for 3 rds', 'C', 'drains'],
  ['Snare', 1, 'Agi -10/X or Snared (escapable)', 'C', 'conditions'],
  ['Spiteful', 1, 'Hatred bonus +X more', 'M', 'stance'],
  ['Standoff', 1, 'Cannot fire inside Xm', 'R', 'accuracy'],
  ['Storm', 0, 'Double hits, double ammo', 'R', 'firepower'],
  ['Sunder', 1, 'Hit crushes X armour at location', 'C', 'bane'],
  ['Suppressing', 1, 'Halts regeneration X rounds', 'C', 'crits'],
  ['Tearing', 0, 'Roll extra d10, drop lowest', 'C', 'damage'],
  ['Telescopic', 0, 'Aimed: no Long/Extreme penalty', 'R', 'accuracy'],
  ['Tidebreaker', 0, 'Hits count double vs hordes', 'C', 'hordes'],
  ['Titanbane', 1, '+X dmg per size above Average', 'C', 'predicates'],
  ['Telegraphed', 1, 'Defender reactions at +X', 'C', 'defence'],
  ['Temperamental', 1, 'Jams on X+', 'R', 'ammo'],
  ['Toxic', 1, 'Tough test -10/X or 1d10 extra', 'C', 'conditions'],
  ['TwinLinked', 0, '+20 hit; 2+ DoS +1 hit; 2x ammo', 'R', 'firepower'],
  ['Unbalanced', 0, '-10 Parry, no Lightning', 'M', 'defence'],
  ['Unreliable', 0, 'Jam on 91+', 'R', 'ammo'],
  ['Unerring', 0, 'Cannot be dodged', 'C', 'defence'],
  ['Unwieldy', 0, 'No Parry or Lightning Attack', 'C', 'defence'],
  ['Vengeful', 1, 'RF triggers on X+', 'C', 'crits'],
  ['Volatile', 0, 'Damage 10s explode (one extra d10)', 'C', 'damage'],
  ['Witchbane', 1, '+X dmg & Pen vs psykers', 'C', 'predicates'],
  ['Withering', 1, 'Toughness bonus -X, 3 rds (tests -10 each)', 'C', 'drains'],
  ['Warded', 0, 'Immune to Power Field destruction', 'M', 'fields'],
  ['WarpWeapon', 0, 'Mundane AP=0, FF still works', 'C', 'bane'],
];

/** Filter WS_LIST by weapon type. Returns specials applicable to the given weapon type. */
export function getSpecialsForWeaponType(weaponType: 'melee' | 'ranged'): typeof WS_LIST {
  return WS_LIST.filter(([, , , cat]) => cat === 'C' || (weaponType === 'melee' ? cat === 'M' : cat === 'R'));
}

/**
 * Specials selectable for a weapon in the editor UIs.
 * By default (RAW) restricts by melee/ranged category; when `allowCrossType`
 * is set (the "shared special rules" house rule), the full list is offered so
 * either weapon type can take any special (e.g. Melta on a melee weapon).
 */
export function getSelectableSpecials(weaponType: 'melee' | 'ranged', allowCrossType: boolean): typeof WS_LIST {
  return allowCrossType ? WS_LIST : getSpecialsForWeaponType(weaponType);
}

/** The family a rule belongs to, or '' for an id nobody knows. */
export function wsFamily(id: string): WSFamily | '' {
  const entry = WS_LIST.find(([wsId]) => wsId === id);
  return entry ? entry[4] : '';
}

/**
 * Group rows for the picker: WS_FAMILIES order, empty families dropped.
 * Takes any WS_LIST subset (the selectable list, a filter's survivors).
 */
export function groupByFamily(rows: typeof WS_LIST): { family: (typeof WS_FAMILIES)[number]; rows: typeof WS_LIST }[] {
  return WS_FAMILIES.map((family) => ({ family, rows: rows.filter((ws) => ws[4] === family.id) })).filter(
    (g) => g.rows.length > 0,
  );
}

export const WS_LABELS: Record<string, string> = {
  RazorSharp: 'Razor Sharp',
  WarpWeapon: 'Warp Weapon',
  TwinLinked: 'Twin-Linked',
  NaturalWeapon: 'Natural Weapon',
  PowerField: 'Power Field',
};

export function wsLabel(id: string): string {
  return WS_LABELS[id] || id;
}

/**
 * The rules blurb for a special, or '' for an id nobody knows.
 *
 * Lived privately in EffectiveProfileCard until v8.189.0; the weapon plate
 * needs the same answer for its chip tooltips, and two copies of a lookup
 * over WS_LIST is one copy too many.
 */
export function wsDescription(id: string): string {
  const entry = WS_LIST.find(([wsId]) => wsId === id);
  return entry ? entry[2] : '';
}

/**
 * A special as a readable rule (plan armoury): "Brawling (2): +2 hit at PB/Short range". The blurb's X is the
 * rule's own value where it has one; a rule nobody knows says so rather than nothing.
 */
export function wsSentence(rule: string): string {
  const { id, p } = parseWS(rule);
  const blurb = wsDescription(id);
  const name = p ? `${wsLabel(id)} (${p})` : wsLabel(id);
  if (!blurb) return `${name}: not in the rules list`;
  return `${name}: ${p ? blurb.replace(/\bX\b/g, String(p)) : blurb}`;
}

/** The label spellings back to their ids - `Razor Sharp` is `RazorSharp` (v8.310.21). */
const WS_IDS_BY_LABEL: Record<string, string> = Object.fromEntries(
  Object.entries(WS_LABELS).map(([id, label]) => [label, id]),
);

/**
 * A rule string to its id and parameter. A rule may arrive under its LABEL -
 * an import, an older sheet, a test typing what the picker shows - and
 * `hasWS(sp, 'RazorSharp')` used to be false for `'Razor Sharp'`, so the
 * pen never doubled and nothing said so (v8.310.21). Labels normalise here,
 * once, for every reader.
 */
export function parseWS(str: string): { id: string; p: number } {
  const m = str.match(/^(.+?)\((\d+)\)$/);
  const raw = m ? m[1] : str;
  const id = WS_IDS_BY_LABEL[raw] ?? raw;
  return m ? { id, p: parseInt(m[2]) } : { id, p: 0 };
}

export function hasWS(sp: string[], id: string): boolean {
  return sp.some((s) => parseWS(s).id === id);
}

export function getWSP(sp: string[], id: string): number {
  const f = sp.find((s) => parseWS(s).id === id);
  return f ? parseWS(f).p : 0;
}

export function fmtWS(str: string): string {
  const p = parseWS(str);
  return wsLabel(p.id) + (p.p > 0 ? '(' + p.p + ')' : '');
}

/**
 * Accurate's bonus damage dice: +1d10 per 2 DoS, max +2d10 - official
 * gating: an AIMED single shot only (v8.197.14; until then any single
 * shot qualified). The single shared gate for the roll path AND both
 * probability engines - they disagreed until v8.196.2 (forecasts also
 * granted it on swift attacks, promising dice the roll would never add).
 */
export function accurateBonusDice(sp: string[], fireMode: string, dos: number, aimed: boolean = false): number {
  if (!hasWS(sp, 'Accurate') || fireMode !== 'single' || !aimed) return 0;
  return Math.min(Math.floor(dos / 2), 2);
}

/** A group that has rolled dice to transform (not a flat bonus, not an empty group). */
const hasRolls = (g: DiceGroup): g is DiceGroup & { rolls: number[] } =>
  g.type !== 'flat' && !!g.rolls && g.rolls.length > 0;

/** A rolled group of d10s - the dice Tearing and Volatile act on. */
const isRolledD10 = (g: DiceGroup): g is DiceGroup & { rolls: number[] } =>
  (g.type === 'dice' || g.type === 'nested') && g.sides === 10 && hasRolls(g);

const sumRolls = (rolls: number[]) => rolls.reduce((a, b) => a + b, 0);

/** The groups as a result: flat bonuses by value, dice by their (re)computed total. */
const resultOf = (nr: DiceGroup[]): DiceResult => ({
  results: nr,
  total: nr.reduce((s, g) => s + (g.type === 'flat' ? g.value! : g.total || 0), 0),
});

/** Tearing: roll one extra d10 on the first d10 group and drop its lowest die. */
export function applyTearing(dr: DiceResult): DiceResult {
  const nr: DiceGroup[] = deepClone(dr.results);
  const g = nr.find(isRolledD10);
  if (g) {
    g.rolls.push(rollDie(10));
    const mi = g.rolls.indexOf(Math.min(...g.rolls));
    g.tornDie = g.rolls[mi];
    g.rolls.splice(mi, 1);
    g.total = sumRolls(g.rolls);
  }
  return resultOf(nr);
}

/**
 * Volatile (v8.284.0): every d10 showing a natural 10 rolls ONE extra
 * d10 and adds it. Bounded by design - the extra dice never explode
 * again, so the seeded E2E dice sequence stays finite and predictable.
 * Applied after Tearing/Proven/Primitive, whose transforms can create
 * or cap tens first.
 */
export function applyVolatile(dr: DiceResult): DiceResult {
  const nr: DiceGroup[] = deepClone(dr.results);
  for (const g of nr.filter(isRolledD10)) {
    const tens = g.rolls.filter((r) => r === 10).length;
    for (let i = 0; i < tens; i++) g.rolls.push(rollDie(10));
    g.total = sumRolls(g.rolls);
  }
  return resultOf(nr);
}

/** Proven(X) raises every die below X to X ('min'); Primitive(X) caps every die above X ('max'). */
export function applyMinMax(dr: DiceResult, x: number, mode: 'min' | 'max'): DiceResult {
  const clamp = mode === 'min' ? (r: number) => (r < x ? x : r) : (r: number) => (r > x ? x : r);
  const nr: DiceGroup[] = deepClone(dr.results);
  for (const g of nr.filter(hasRolls)) {
    g.rolls = g.rolls.map(clamp);
    g.total = sumRolls(g.rolls);
  }
  return resultOf(nr);
}
